<template>
  <div class="text-left w-full flex flex-col address-modal-container">
    <!-- Header Modal Mobile -->
    <div class="flex items-center gap-4 mb-6 mdmax:mb-4">
      <span class="cursor-pointer hidden mdmax:block" @click="$emit('close')">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M15 18L9 12L15 6" stroke="#2D3748" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </span>
      <p class="text-2xl font-bold mdmax:text-xl mdmax:text-center mdmax:flex-1 mdmax:-ml-8">{{ title }}</p>
    </div>

    <div class="overflow-y-auto flex-1 pr-2 -mr-2 pb-4">
      <!-- Alamat Tersimpan -->
      <div class="mb-6">
        <div class="flex justify-between items-center mb-4">
          <p class="font-bold text-neutral-800">Alamat Tersimpan</p>
        </div>
        <div class="mb-4 relative">
          <TextField 
            withIcon 
            type="text" 
            v-model="savedAddressSearchKeyword" 
            placeholder="Cari alamat tersimpan..." 
          />
          <div class="absolute left-3 top-1/2 transform -translate-y-1/2 cursor-pointer pointer-events-none">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 17A8 8 0 1 0 9 1a8 8 0 0 0 0 16z" stroke="#A0AEC0" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M19 19l-4.35-4.35" stroke="#A0AEC0" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
        </div>
        <div class="flex overflow-x-auto gap-4 pb-2 snap-x hide-scrollbar">
          <div 
            v-for="(addr, index) in filteredAddresses" 
            :key="'saved-addr-' + index"
            @click="selectSavedAddress(addr)"
            class="flex-shrink-0 w-56 p-4 border-2 rounded-xl cursor-pointer snap-start transition-all"
            :class="selectedAddressId === addr.id ? 'border-primary-500 bg-primary-50 shadow-sm' : 'border-neutral-300 hover:border-primary-400 bg-white'"
          >
            <p class="text-xs text-neutral-500 mb-1">{{ addr.location_label || 'Label Alamat' }}</p>
            <p class="font-bold text-neutral-800 mb-1 truncate">{{ addr.sender_name || addr.receiver_name || addr.name }}</p>
            <p class="text-xs text-neutral-600 line-clamp-2 leading-tight mb-1">{{ addr.address }}</p>
            <p class="text-xs text-neutral-500 truncate">{{ addr.area ? (addr.area.village + ', ' + addr.area.area_display) : '' }}</p>
          </div>
          <div v-if="!filteredAddresses || filteredAddresses.length === 0" class="text-sm text-neutral-500 py-4 w-full text-center border border-dashed border-neutral-300 rounded-xl">
            Belum ada alamat tersimpan
          </div>
        </div>
      </div>

      <!-- Form Manual -->
      <div class="p-5 bg-white rounded-xl border border-neutral-100 shadow-sm">
        <p class="font-bold text-neutral-800 mb-4">{{ formTitle }}</p>

        <div class="mb-4">
          <TextField 
            type="text" 
            label="Label *" 
            v-model="form.label" 
            placeholder="Contoh: Rumah" 
          />
        </div>

        <div class="mb-4">
          <TextField 
            type="text" 
            label="Nama *" 
            v-model="form.name" 
            placeholder="Contoh: Linknau" 
          />
        </div>

        <div class="mb-4">
          <TextField 
            type="text" 
            @keypress="numbersOnly" 
            @input="validatePhoneNumber"
            @blur="onPhoneBlur"
            label="No. Handphone *" 
            v-model="form.phone" 
            placeholder="08123456789" 
          />
        </div>

        <div class="mb-4 relative">
          <label class="text-sm text-neutral-700 font-medium mb-1 inline-block">Area *</label>

          <!-- Dropdown Trigger -->
          <div 
            class="w-full border rounded-lg p-3 pr-10 cursor-pointer flex items-center justify-between"
            :class="[visibleSuggestion ? 'border-primary-500 ring-1 ring-primary-500' : 'border-neutral-300', form.area ? 'bg-primary-50 border-primary-500' : 'bg-white']"
            @click="toggleAreaDropdown"
          >
            <div v-if="form.area">
              <p class="font-bold text-neutral-800 text-sm">{{ form.area.village }}</p>
              <p class="text-xs text-neutral-500">{{ form.area.area_display }}</p>
            </div>
            <div v-else class="text-neutral-400 text-sm">
              Pilih area...
            </div>
            <div class="absolute right-3 top-1/2 transform -translate-y-1/2">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="#A0AEC0" stroke-width="1.5">
                <path d="M5 7.5L10 12.5L15 7.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
          </div>

          <!-- Invisible overlay to close dropdown -->
          <div 
            v-if="visibleSuggestion" 
            class="fixed inset-0 z-10" 
            @click="visibleSuggestion = false"
          ></div>

          <!-- Dropdown Content -->
          <transition name="fade" mode="out-in">
            <div v-if="visibleSuggestion" class="mt-1 z-20 border rounded-lg absolute top-full left-0 w-full bg-white shadow-lg overflow-hidden flex flex-col">
              
              <!-- Search Input -->
              <div class="p-3 border-b border-neutral-100 relative">
                <div class="relative flex items-center">
                  <div class="absolute left-3 text-neutral-400">
                    <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M9 17A8 8 0 1 0 9 1a8 8 0 0 0 0 16z"/>
                      <path d="M19 19l-4.35-4.35"/>
                    </svg>
                  </div>
                  <input 
                    type="text" 
                    class="w-full bg-neutral-50 border border-neutral-200 rounded-lg py-2 pl-10 pr-10 text-sm focus:outline-none focus:border-primary-500 transition-colors"
                    placeholder="Cari area..."
                    v-model="citySearch"
                    @input="searchCity"
                    ref="areaSearchInput"
                  />
                  <img src="@/assets/images/rolling.gif" class="w-5 absolute right-3" v-if="isSearchingCity" />
                </div>
              </div>

              <!-- List of Areas -->
              <ul class="max-h-56 overflow-y-auto">
                <!-- Selected Area (Checked) -->
                <li 
                  v-if="form.area && matchesSearch(form.area)"
                  class="cursor-pointer px-4 py-3 border-b border-neutral-100 flex items-center justify-between bg-white hover:bg-neutral-50"
                  @click="selectCity(form.area)"
                >
                  <div>
                    <p class="font-bold text-neutral-800 text-sm">{{ form.area.village }}</p>
                    <p class="text-xs text-neutral-500">{{ form.area.area_display }}</p>
                  </div>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#10B981" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                </li>

                <!-- Other Areas -->
                <li 
                  v-for="(city, index) in filteredCitiesList" 
                  :key="'city-' + index" 
                  class="cursor-pointer px-4 py-3 border-b border-neutral-100 last:border-b-0 hover:bg-neutral-50 transition-colors" 
                  @click="selectCity(city)"
                >
                  <div>
                    <p class="font-bold text-neutral-800 text-sm">{{ city.village }}</p>
                    <p class="text-xs text-neutral-500">{{ city.area_display }}</p>
                  </div>
                </li>

                <li v-if="!isSearchingCity && filteredCitiesList.length === 0 && (!form.area || !matchesSearch(form.area))" class="text-center py-6 text-neutral-500 text-sm">
                  Area tidak ditemukan
                </li>
              </ul>
            </div>
          </transition>
        </div>

        <div class="mb-4">
          <Textarea 
            label="Alamat *" 
            v-model="form.address" 
            placeholder="Tulis alamat" 
          />
        </div>

        <div class="flex items-center gap-2 mb-2">
          <input type="checkbox" id="saveAddress" v-model="form.saveAddress" class="w-4 h-4 rounded border-neutral-300 text-primary-500 focus:ring-primary-500 cursor-pointer">
          <label for="saveAddress" class="text-sm font-medium text-neutral-700 cursor-pointer">Simpan Alamat</label>
        </div>
      </div>
    </div>

    <div class="mt-auto pt-4 border-t border-neutral-100 bg-white z-10">
      <Button 
        class="w-full" 
        buttonText="Gunakan Alamat Ini" 
        type="primary" 
        :disabled="isDisabled || isSaving" 
        :isLoading="isSaving"
        @action="applyAddress()" 
      />
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

export default {
  name: 'AddressFormModal',
  components: {
    TextField: () => import(/* webpackChunkName: "Forms" */ '@/components/Forms/TextField'),
    Textarea: () => import(/* webpackChunkName: "Forms" */ '@/components/Forms/Textarea'),
    Button: () => import(/* webpackChunkName: "Forms" */ '@/components/Forms/Button')
  },
  props: {
    type: {
      type: String, // 'sender' | 'receiver'
      default: 'sender'
    },
    initialData: {
      type: Object,
      default: () => null
    }
  },
  data() {
    return {
      selectedAddressId: null,
      selectedSavedAddressObj: null,
      
      form: {
        label: '',
        name: '',
        phone: '',
        area: null, // object city
        address: '',
        saveAddress: false
      },
      citySearch: '',
      savedAddressSearchKeyword: '',
      
      isSearchingCity: false,
      visibleSuggestion: false,
      debounce: null,
      isSaving: false,
    }
  },
  computed: {
    ...mapGetters('master', ['cities']),
    ...mapGetters('merchant', ['addresses']),
    filteredAddresses() {
      if (!this.savedAddressSearchKeyword) return this.addresses;
      const lowerKeyword = this.savedAddressSearchKeyword.toLowerCase();
      return this.addresses.filter(addr => {
        const name = (addr.sender_name || addr.receiver_name || addr.name || '').toLowerCase();
        const address = (addr.address || '').toLowerCase();
        const label = (addr.location_label || '').toLowerCase();
        return name.includes(lowerKeyword) || address.includes(lowerKeyword) || label.includes(lowerKeyword);
      });
    },
    filteredCitiesList() {
      let list = this.cities || [];
      if (this.form.area) {
        list = list.filter(c => {
          if (c.code && this.form.area.code) return c.code !== this.form.area.code;
          if (c.id && this.form.area.id) return c.id !== this.form.area.id;
          return c.village !== this.form.area.village || c.area_display !== this.form.area.area_display;
        });
      }
      return list;
    },
    
    title() {
      return this.type === 'sender' ? 'Pengirim' : 'Penerima'
    },
    formTitle() {
      return this.type === 'sender' ? 'Informasi Pengirim' : 'Informasi Penerima'
    },
    isFormFilled() {
      const cleanPhone = this.normalizePhoneNumber(this.form.phone)
      return !!(
        this.form.name && 
        cleanPhone && 
        cleanPhone.length >= 8 && 
        cleanPhone.length <= 15 && 
        this.form.area && 
        this.form.address
      )
    },
    isDisabled() {
      // Tombol aktif jika user memilih dari saved address ATAU mengisi form dengan lengkap
      return !this.selectedAddressId && !this.isFormFilled
    }
  },
  watch: {
    initialData: {
      immediate: true,
      handler(val) {
        if (val) {
          if (val.isSavedAddress) {
            this.selectedAddressId = val.addressObj.id
            this.selectedSavedAddressObj = val.addressObj
            
            this.form.label = val.addressObj.location_label || ''
            this.form.name = val.addressObj.sender_name || val.addressObj.receiver_name || val.addressObj.name || ''
            this.form.phone = this.normalizePhoneNumber(val.addressObj.sender_phone || val.addressObj.phone || '')
            this.form.area = val.addressObj.area || null
            this.form.address = val.addressObj.address || ''
            this.citySearch = ''
          } else {
            this.form.label = val.data?.label || ''
            this.form.name = val.data?.name || ''
            this.form.phone = this.normalizePhoneNumber(val.data?.phone || '')
            this.form.area = val.data?.area || null
            this.form.address = val.data?.address || ''
            this.citySearch = ''
          }
        }
      }
    }
  },
  mounted() {
    // Memuat daftar alamat tersimpan
    this.getAddresses({
      params: {
        location_label: ''
      }
    })
  },
  methods: {
    ...mapActions('master', ['getCities']),
    ...mapActions('merchant', ['getAddresses']),
    
    selectSavedAddress(addr) {
      this.selectedAddressId = addr.id
      this.selectedSavedAddressObj = addr
      
      // Auto-fill manual form if user selects saved address
      this.form = {
        label: addr.location_label || '',
        name: addr.sender_name || addr.receiver_name || addr.name || '',
        phone: this.normalizePhoneNumber(addr.sender_phone || addr.phone || ''),
        area: addr.area || null,
        address: addr.address || '',
        saveAddress: false
      }
      
      this.citySearch = ''
    },
    normalizePhoneNumber(phone) {
      if (!phone) return ''
      let digits = String(phone).replace(/\D/g, '')
      if (digits.startsWith('0062')) {
        digits = digits.slice(4)
        if (digits.startsWith('0')) digits = digits.slice(1)
        digits = '0' + digits
      } else if (digits.startsWith('620')) {
        digits = '0' + digits.slice(3)
      } else if (digits.startsWith('62')) {
        digits = '0' + digits.slice(2)
      } else if (digits.startsWith('8')) {
        digits = '0' + digits
      }
      return digits
    },
    
    clearSelection() {
      this.selectedAddressId = null
      this.selectedSavedAddressObj = null
    },
    
    numbersOnly(evt) {
      evt = evt ? evt : window.event
      var charCode = evt.which ? evt.which : evt.keyCode
      if (charCode > 31 && (charCode < 48 || charCode > 57) && charCode !== 46) {
        evt.preventDefault()
      } else {
        return true
      }
    },
    
    validatePhoneNumber(val) {
      const raw = typeof val === 'object' && val !== null && val.target ? val.target.value : String(val || '')
      if (raw.includes('+') || raw.startsWith('62') || raw.includes('-') || raw.includes(' ')) {
        const normalized = this.normalizePhoneNumber(raw)
        this.$nextTick(() => {
          this.form.phone = normalized.slice(0, 15)
        })
      } else {
        const digits = raw.replace(/\D/g, '').slice(0, 15)
        this.$nextTick(() => {
          this.form.phone = digits
        })
      }
    },

    onPhoneBlur() {
      if (this.form.phone) {
        this.form.phone = this.normalizePhoneNumber(this.form.phone)
      }
    },
    
    searchCity() {
      this.isSearchingCity = true
      clearTimeout(this.debounce)
      this.debounce = setTimeout(() => {
        this.getCities({
          params: {
            page: 1,
            per_page: 100,
            search_keyword: this.citySearch || ''
          }
        }).then(() => {
          this.visibleSuggestion = true
          this.isSearchingCity = false
        }).catch(() => {
          this.isSearchingCity = false
        })
      }, 500)
    },
    
    toggleAreaDropdown() {
      this.visibleSuggestion = !this.visibleSuggestion;
      if (this.visibleSuggestion) {
        this.$nextTick(() => {
          if (this.$refs.areaSearchInput) {
            this.$refs.areaSearchInput.focus();
          }
        });
        if (!this.cities || this.cities.length === 0) {
          this.searchCity();
        }
      }
    },
    
    matchesSearch(area) {
      if (!this.citySearch) return true;
      const term = this.citySearch.toLowerCase();
      return area.village.toLowerCase().includes(term) || area.area_display.toLowerCase().includes(term);
    },
    
    selectCity(city) {
      this.form.area = city
      this.citySearch = ''
      this.visibleSuggestion = false
    },
    
    async applyAddress() {
      const normalizedPhone = this.normalizePhoneNumber(this.form.phone)
      this.form.phone = normalizedPhone

      if (this.form.saveAddress) {
        this.isSaving = true
        try {
          const payload = {
            location_label: this.form.label || null,
            name: this.form.name || null,
            sender_name: this.form.name || null,
            receiver_name: this.form.name || null,
            phone: normalizedPhone || null,
            sender_phone: normalizedPhone || null,
            address: this.form.address || null,
            detail_address: null,
            area_id: this.form.area ? this.form.area.id : null,
            area_code: this.form.area ? this.form.area.code : null,
            is_main: false
          }
          
          if (this.selectedAddressId) {
            await this.$store.dispatch('merchant/editAddress', {
              addressId: this.selectedAddressId,
              payloads: payload
            })
          } else {
            await this.$store.dispatch('merchant/addAddress', {
              payloads: payload
            })
          }
          // Refresh addresses list
          await this.getAddresses({ params: { location_label: '' } })
        } catch (error) {
          console.error('Failed to save address:', error)
        } finally {
          this.isSaving = false
        }
      }
      this.$emit('apply', {
        isSavedAddress: !!this.selectedAddressId,
        addressObj: this.selectedAddressId 
          ? { 
              ...this.selectedSavedAddressObj, 
              ...this.form, 
              location_label: this.form.label, 
              sender_name: this.form.name, 
              receiver_name: this.form.name, 
              sender_phone: normalizedPhone, 
              phone: normalizedPhone 
            } 
          : null,
        data: { ...this.form, phone: normalizedPhone }
      })
    }
  }
}
</script>

<style scoped>
.hide-scrollbar::-webkit-scrollbar {
  display: none;
}
.hide-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.address-modal-container {
  height: 75vh;
}
@media (min-width: 768px) {
  .address-modal-container {
    height: 600px;
  }
}
</style>
